import { BZE_ARTIFACT_TYPE_MARKDOWN, BZE_ARTIFACT_TYPE_HTML, BZE_ARTIFACT_TYPE_CODE, BZE_ARTIFACT_TYPE_CSV, BZE_ARTIFACT_TYPE_TEXT, BZE_ARTIFACT_TYPE_EMAIL, BZE_ARTIFACT_TYPE_LANDING_PAGE, BZE_ARTIFACT_TYPE_BLOG_POST, BZE_ARTIFACT_TYPE_SITE_PAGE, BZE_ARTIFACT_TYPE_REPORT, BZE_ARTIFACT_TYPE_DATA_MODEL } from 'breeze-engine-core/constants';
const ARTIFACTS_DIR = '/artifacts';
const FILE_TOOLS = new Set(['write', 'edit', 'multiedit']);
export function isFileArtifactTool(name) {
  return FILE_TOOLS.has(name.toLowerCase());
}
export function isArtifactsPath(filePath) {
  const normalized = filePath.replace(/\\/g, '/');
  return normalized === ARTIFACTS_DIR || normalized.startsWith(`${ARTIFACTS_DIR}/`);
}
function basename(filePath) {
  const normalized = filePath.replace(/\\/g, '/');
  const slash = normalized.lastIndexOf('/');
  return slash >= 0 ? normalized.slice(slash + 1) : normalized;
}
function normalizeDataType(raw) {
  return raw.toLowerCase().replace(/[_-]/g, '-');
}
const DATA_TYPE_TO_CANONICAL = {
  markdown: BZE_ARTIFACT_TYPE_MARKDOWN,
  html: BZE_ARTIFACT_TYPE_HTML,
  code: BZE_ARTIFACT_TYPE_CODE,
  csv: BZE_ARTIFACT_TYPE_CSV,
  text: BZE_ARTIFACT_TYPE_TEXT,
  email: BZE_ARTIFACT_TYPE_EMAIL,
  'email-draft': BZE_ARTIFACT_TYPE_EMAIL,
  'landing-page': BZE_ARTIFACT_TYPE_LANDING_PAGE,
  'blog-post': BZE_ARTIFACT_TYPE_BLOG_POST,
  'site-page': BZE_ARTIFACT_TYPE_SITE_PAGE,
  report: BZE_ARTIFACT_TYPE_REPORT,
  'data-model': BZE_ARTIFACT_TYPE_DATA_MODEL
};
const EXTENSION_TO_CANONICAL = {
  md: BZE_ARTIFACT_TYPE_MARKDOWN,
  html: BZE_ARTIFACT_TYPE_HTML,
  htm: BZE_ARTIFACT_TYPE_HTML,
  csv: BZE_ARTIFACT_TYPE_CSV,
  txt: BZE_ARTIFACT_TYPE_TEXT,
  py: BZE_ARTIFACT_TYPE_CODE,
  js: BZE_ARTIFACT_TYPE_CODE,
  ts: BZE_ARTIFACT_TYPE_CODE,
  jsx: BZE_ARTIFACT_TYPE_CODE,
  tsx: BZE_ARTIFACT_TYPE_CODE,
  json: BZE_ARTIFACT_TYPE_CODE,
  yaml: BZE_ARTIFACT_TYPE_CODE,
  yml: BZE_ARTIFACT_TYPE_CODE
};
function deslugify(slug) {
  return slug.split(/[-_.]+/).filter(segment => segment.length > 0).map(segment => segment.charAt(0).toUpperCase() + segment.slice(1)).join(' ');
}
export function parseArtifactFilename(filePath) {
  var _EXTENSION_TO_CANONIC;
  const name = basename(filePath);
  if (name.length === 0) return null;
  const segments = name.split('.');
  if (segments.length < 2) return null;
  const format = segments[segments.length - 1].toLowerCase();
  if (format.length === 0) return null;
  if (segments.length >= 3) {
    var _ref, _DATA_TYPE_TO_CANONIC;
    const rawDataType = segments[segments.length - 2];
    const normalized = normalizeDataType(rawDataType);
    const namePart = segments.slice(0, -2).join('.');
    if (namePart.length === 0) return null;
    // A recognized data-type segment always wins (e.g. landing-page, blog-post).
    // When it is unrecognized, self-describing formats (html, md, csv, …) fall
    // back to the file extension so a stray segment like `page` in
    // `product-launch.page.html` reads as HTML rather than the bogus type PAGE.
    // `.json` is a container format whose extension carries no type, so its
    // unrecognized segment is surfaced as the type as-is.
    const extensionFallback = format === 'json' ? undefined : EXTENSION_TO_CANONICAL[format];
    const canonical = (_ref = (_DATA_TYPE_TO_CANONIC = DATA_TYPE_TO_CANONICAL[normalized]) !== null && _DATA_TYPE_TO_CANONIC !== void 0 ? _DATA_TYPE_TO_CANONIC : extensionFallback) !== null && _ref !== void 0 ? _ref : normalized.toUpperCase().replace(/-/g, '_');
    return {
      name: namePart,
      dataType: canonical,
      format,
      title: deslugify(namePart)
    };
  }
  const namePart = segments[0];
  if (namePart.length === 0) return null;
  const inferred = (_EXTENSION_TO_CANONIC = EXTENSION_TO_CANONICAL[format]) !== null && _EXTENSION_TO_CANONIC !== void 0 ? _EXTENSION_TO_CANONIC : BZE_ARTIFACT_TYPE_TEXT;
  return {
    name: namePart,
    dataType: inferred,
    format,
    title: deslugify(namePart)
  };
}
export function deriveArtifactAction(toolName) {
  if (toolName.toLowerCase() === 'write') return 'created';
  return 'updated';
}
function normalizeForMatch(value) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

// The backend derives an artifact's stored `title` from the written filename,
// but its deslugify (keeps unrecognized segments like `.page`, splits only on
// hyphens) differs from ours. To resolve a filesystem artifact to its stored
// record we compare the backend title against the filename stripped of its
// extension, and again stripped of the data-type segment too — normalized so
// punctuation/casing/segment differences don't matter. Covers both
// `product-launch-announcement.page.html` ("…Announcement.page") and
// `q3-report.markdown.md` ("Q3 Report", data-type segment dropped).
export function artifactTitleMatchesPath(title, filePath) {
  const target = normalizeForMatch(title);
  if (target.length === 0) return false;
  const segments = basename(filePath).split('.');
  const candidates = [];
  if (segments.length >= 2) candidates.push(segments.slice(0, -1).join('.'));
  if (segments.length >= 3) candidates.push(segments.slice(0, -2).join('.'));
  return candidates.some(candidate => normalizeForMatch(candidate) === target);
}